import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from './Attendance.module.css';
import { getTherapistTimings, saveEmployeeAttendance, updateCheckinCheckout, getTimingCodes } from '../../../../APICalling/APICalling';
import { buildTimeDropdownOptions, doesTimeRangeCrossMidnight, formatDateForBackend, getCurrentTimeInTimezone, getDateKeyInTimezone, getExtendedDropdownSelectedValue } from '../../../../Utils/utils';
import PrimaryButton from '../../../../Components/Buttons/PrimaryButton';
import Loader from '../../../../Components/Loader/Loader';
import deleteIcon from '../../../../Assets/images/delete.svg';
import { toast } from 'react-toastify';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import DropdownNoLabel from '../../../../Components/DropDown/DropdownNoLabel';
import Echo from '../../../../Components/Sockect/Echo';

// Module-level cache for timing codes (persists across component re-renders)
let cachedTimingCodes = null;

const Attendance = ({ therapistId, startDate, onAttendanceUpdate }) => {
    const { selectedOutlet, loggedOutletID } = useGlobalContext();
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false); // For data fetching (shows loader)
    const [actionLoading, setActionLoading] = useState(false); // For check-in/check-out/update actions (no loader)
    const [editedTimes, setEditedTimes] = useState({});
    const [timingCodes, setTimingCodes] = useState(cachedTimingCodes || {});
    const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open: 'index-field'
    const attendanceChannelRef = useRef(null);

    // Generate 7 days starting from startDate
    const generateWeekDays = (start) => {
        const days = [];
        const startDateObj = new Date(start);
        
        for (let i = 0; i < 7; i++) {
            const currentDate = new Date(startDateObj);
            currentDate.setDate(startDateObj.getDate() + i);
            // Strip clock time so day-of-week / outlet-extended lookups stay on this calendar day
            currentDate.setHours(0, 0, 0, 0);
            
            const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
            const dateStr = currentDate.getDate();
            const monthStr = currentDate.toLocaleDateString('en-US', { month: 'short' });
            const formattedDate = formatDateForBackend(currentDate);
            
            days.push({
                dayName,
                dateStr,
                monthStr,
                formattedDate,
                fullDate: currentDate
            });
        }
        
        return days;
    };

    // Row calendar key from local Y/M/D (same as label / formattedDate).
    // Do not run the Date instant through SGT — evening local times would shift the day (+1 day).
    const getAttendanceRowDateKey = (dateInput) => {
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (!date || Number.isNaN(date.getTime())) return '';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const getSgtTodayKey = () => getDateKeyInTimezone(new Date());

    const [weekDays, setWeekDays] = useState([]);

    // Fetch timing codes only once (using module-level cache)
    useEffect(() => {
        if (!cachedTimingCodes) {
            fetchTimingCodes();
        }
    }, []);

    const fetchTimingCodes = async () => {
        try {
            const outletId = selectedOutlet || loggedOutletID;
            const response = await getTimingCodes({ 
                pagination: 0,
                outlet: outletId
            });
            
            if (response?.data?.success) {
                const codes = response.data.data?.list?.timing_code || [];
                // Create a map of id -> code for easy lookup
                const codesMap = codes.reduce((acc, code) => {
                    acc[code.id] = code.code;
                    return acc;
                }, {});
                
                // Cache at module level
                cachedTimingCodes = codesMap;
                setTimingCodes(codesMap);
            }
        } catch (error) {
            console.error('Error fetching timing codes:', error);
        }
    };

    useEffect(() => {
        if (startDate) {
            const days = generateWeekDays(startDate);
            setWeekDays(days);
        }
    }, [startDate]);

    useEffect(() => {
        if (therapistId && weekDays.length > 0) {
            fetchAttendanceData();
        }
    }, [therapistId, weekDays, selectedOutlet, loggedOutletID]);

    useEffect(() => {
        if (!Echo || !therapistId) return;

        const channelName = 'attendance';
        const eventName = 'attendance.updated';

        const normalizeEventDate = (rawDate) => {
            if (!rawDate || typeof rawDate !== 'string') return null;
            if (rawDate.includes('-')) {
                const parts = rawDate.split('-');
                if (parts[0]?.length === 4) {
                    const [year, month, day] = parts;
                    return `${day}-${month}-${year}`;
                }
                return rawDate;
            }
            return rawDate;
        };

        const handleEvent = (event) => {
            try {
                const envelope = event?.data || event;
                const payload = envelope?.data || envelope;
                if (!payload) return;

                const payloadTherapistId = payload.therapist_id || payload.user_id || payload.user;
                if (!payloadTherapistId || String(payloadTherapistId) !== String(therapistId)) return;

                const eventDateRaw = payload.activity_date || payload.event_date || payload.date;
                const eventDate = normalizeEventDate(eventDateRaw);
                if (!eventDate) return;
                fetchAttendanceData(false);
            } catch (error) {
                console.error('Error in attendance socket handler:', error);
            }
        };

        try {
            const channel = Echo.channel(channelName);
            attendanceChannelRef.current = channel;

            channel.listen(eventName, handleEvent);
            channel.listen(`.${eventName}`, handleEvent);
        } catch (error) {
            console.error('Error setting up attendance socket listener:', error);
        }

        return () => {
            if (attendanceChannelRef.current) {
                attendanceChannelRef.current.stopListening(eventName);
                attendanceChannelRef.current.stopListening(`.${eventName}`);
            }
        };
    }, [therapistId, weekDays]);

    const fetchAttendanceData = async (showLoader = true) => {
        if (weekDays.length === 0 || !therapistId) return;
        
        if (showLoader) {
            setLoading(true);
        }
        try {
            const startDateFormatted = weekDays[0].formattedDate;
            const endDateFormatted = weekDays[weekDays.length - 1].formattedDate;

            const response = await getTherapistTimings({
                user: therapistId,
                outlet: selectedOutlet || loggedOutletID,
                start_date: startDateFormatted,
                end_date: endDateFormatted
            });

            if (response?.success && response?.data?.list) {
                // API returns data filtered by therapistId, so we get only this therapist's data
                const therapistData = response.data.list[therapistId];
                
                if (therapistData) {
                    const attendanceArray = weekDays.map(day => {
                        const dayData = therapistData[day.formattedDate]?.[0] || null;
                        return {
                            ...day,
                            schedule: dayData ? {
                                checkin: dayData.checkin,
                                checkout: dayData.checkout,
                                timing_code_id: dayData.timing_code_id
                            } : null,
                            checkin_daily: dayData?.checkin_daily || null,
                            checkout_daily: dayData?.checkout_daily || null,
                            is_schedule_calendar_expansion: dayData?.is_schedule_calendar_expansion || 0,
                            is_checkin_calendar_expansion: dayData?.is_checkin_calendar_expansion || 0,
                            is_checkout_calendar_expansion: dayData?.is_checkout_calendar_expansion || 0,
                            leave: dayData?.leave || 0,
                            timing_code_id: dayData?.timing_code_id || null,
                            status_id: dayData?.status_id || null,
                            status: dayData?.status || null,
                            remark: dayData?.remark || null
                        };
                    });
                    
                    setAttendanceData(attendanceArray);
                } else {
                    // No data for this therapist
                    setAttendanceData(weekDays.map(day => ({
                        ...day,
                        schedule: null,
                        checkin_daily: null,
                        checkout_daily: null,
                        is_schedule_calendar_expansion: 0,
                        is_checkin_calendar_expansion: 0,
                        is_checkout_calendar_expansion: 0,
                        leave: 0,
                        timing_code_id: null,
                        status_id: null,
                        status: null,
                        remark: null
                    })));
                }
            }
        } catch (error) {
            console.error('Error fetching attendance data:', error);
        } finally {
            if (showLoader) {
                setLoading(false);
            }
        }
    };

    // Past / today / future vs SGT calendar "today", using the row's calendar date (not Date-with-time → SGT).
    const isPastDate = (date) => {
        const rowKey = getAttendanceRowDateKey(date);
        const todayKey = getSgtTodayKey();
        return Boolean(rowKey && todayKey && rowKey < todayKey);
    };

    const isTodayOrPast = (date) => {
        const rowKey = getAttendanceRowDateKey(date);
        const todayKey = getSgtTodayKey();
        return Boolean(rowKey && todayKey && rowKey <= todayKey);
    };

    const isFutureDate = (date) => {
        const rowKey = getAttendanceRowDateKey(date);
        const todayKey = getSgtTodayKey();
        return Boolean(rowKey && todayKey && rowKey > todayKey);
    };

    // Format time to HH:mm (remove seconds if present)
    const formatTimeToHHMM = (timeString) => {
        if (!timeString) return null;
        // If time is already in HH:mm format, return as is
        // If time is in HH:mm:ss format, remove the seconds
        const timeParts = timeString.split(':');
        if (timeParts.length >= 2) {
            return `${timeParts[0]}:${timeParts[1]}`;
        }
        return timeString;
    };

    const getTimeOptionsForDate = () => buildTimeDropdownOptions({
        isOutletExtended: true,
        includeNextDay: true,
    });

    // Attendance times from API and dropdowns are SGT — never use browser local time here
    const getCurrentTime = () => getCurrentTimeInTimezone();

    const getButtonText = (dayData, index) => {
        const dayKey = `${index}`;
        const edits = editedTimes[dayKey];
        
        // For past dates, always show "Update"
        if (isPastDate(dayData.fullDate)) {
            return 'Update';
        }
        
        // For today
        // No check-in exists yet (from API)
        if (!dayData.checkin_daily) {
            return 'Check In';
        }
        
        // Check-in exists, no check-out yet (from API)
        if (dayData.checkin_daily && !dayData.checkout_daily) {
            // If check-in is being edited, show "Update"
            if (edits && edits.checkin_daily !== undefined) {
                return 'Update';
            }
            // If check-out is being added, show "Check Out"
            if (edits && edits.checkout_daily !== undefined) {
                return 'Check Out';
            }
            // Default: show "Check Out" (to add check-out)
            return 'Check Out';
        }
        
        // Both exist from API - show update if any edits, otherwise "Update"
        if (edits && (edits.checkin_daily !== undefined || edits.checkout_daily !== undefined)) {
            return 'Update';
        }
        
        // Both exist but no edits
        return 'Update';
    };

    const hasValidEdits = (dayData, index) => {
        const dayKey = `${index}`;
        const edits = editedTimes[dayKey];
        
        // If no edits at all, return false
        if (!edits) return false;
        
        const isPast = isPastDate(dayData.fullDate);
        
        // For past dates: Need at least one field edited
        if (isPast) {
            return edits.checkin_daily !== undefined || edits.checkout_daily !== undefined;
        }
        
        // For today's sequential flow:
        // Case 1: No check-in exists - need checkin_daily edit
        if (!dayData.checkin_daily) {
            return edits.checkin_daily !== undefined && edits.checkin_daily !== null && edits.checkin_daily !== '';
        }
        
        // Case 2: Check-in exists, no check-out - need checkout_daily edit
        if (dayData.checkin_daily && !dayData.checkout_daily) {
            return edits.checkout_daily !== undefined && edits.checkout_daily !== null && edits.checkout_daily !== '';
        }
        
        // Case 3: Both exist - need at least one field edited
        if (dayData.checkin_daily && dayData.checkout_daily) {
            return edits.checkin_daily !== undefined || edits.checkout_daily !== undefined;
        }
        
        return false;
    };

    // Get tooltip message for disabled button
    const getButtonTooltip = (dayData, index, canEdit, isFuture, hasScheduleTimes, hasValid) => {
        // If button is disabled due to missing time selection, show tooltip
        if (canEdit && !isFuture && hasScheduleTimes && dayData.leave !== 1 && !hasValid) {
            return 'Please select time';
        }
        
        // Return existing tooltip message if any
        if (isFuture) {
            return 'Cannot edit future date';
        }
        if (!hasScheduleTimes) {
            return 'Therapist schedule not available';
        }
        if (dayData.leave === 1) {
            return undefined; // No tooltip for leave days
        }
        
        return undefined;
    };

    const handleTimeChange = (index, field, value, fromDropdown = false) => {
        const dayKey = `${index}`;
        const expansionKey = field === 'checkin_daily' ? 'checkin_daily_expansion' : 'checkout_daily_expansion';
        
        // Allow empty value (user deleted everything)
        if (!value || (typeof value === 'string' && value.trim() === '')) {
            setEditedTimes(prev => ({
                ...prev,
                [dayKey]: {
                    ...prev[dayKey],
                    [field]: null
                }
            }));
            return;
        }
        
        // Detect "+1" suffix (next-day time selected from dropdown)
        const isNextDay = typeof value === 'string' && value.endsWith('+1');
        const baseTime = isNextDay ? value.slice(0, -2) : value;
        const formattedValue = `${baseTime}:00`;
        
        setEditedTimes(prev => {
            const updated = {
                ...prev,
                [dayKey]: {
                    ...prev[dayKey],
                    [field]: formattedValue,
                }
            };
            if (fromDropdown) {
                // Dropdown: preserve +1 vs same-day choice explicitly
                updated[dayKey][expansionKey] = isNextDay ? 1 : 0;
            } else {
                // Typed in input: always same-day (no +1 in native time input)
                updated[dayKey][expansionKey] = 0;
            }
            return updated;
        });
    };

    // Track click timing to distinguish single vs double click
    const clickTimersRef = useRef({});

    // Handle single click - open dropdown
    const handleTimeInputClick = (e, index, field, currentValue) => {
        const inputElement = e.target;
        const clickKey = `${index}-${field}`;
        
        // Clear any existing timer for this input
        if (clickTimersRef.current[clickKey]) {
            clearTimeout(clickTimersRef.current[clickKey]);
            delete clickTimersRef.current[clickKey];
        }
        
        // Wait to see if it's a double click
        clickTimersRef.current[clickKey] = setTimeout(() => {
            // Single click - open dropdown (removed auto-fill current time logic)
            
            // Find the sibling dropdown and trigger it
            const inputWrapper = inputElement.closest(`.${styles.timeInputWrapper}`);
            if (inputWrapper) {
                const timeInputGroup = inputWrapper.closest(`.${styles.timeInputGroup}`);
                if (timeInputGroup) {
                    const dropdownWrapper = timeInputGroup.querySelector(`.${styles.timeDropdownWrapper}`);
                    const dropdownToggle = dropdownWrapper?.querySelector('.dropdownToggle');
                    if (dropdownToggle && dropdownWrapper) {
                        // Enable pointer events on wrapper before opening
                        dropdownWrapper.style.pointerEvents = 'auto';
                        
                        dropdownToggle.click();
                        
                        // Find the table row to disable hover effect
                        const tableRow = inputWrapper.closest('tr');
                        
                        // Use MutationObserver or setTimeout to ensure dropdown menu is visible
                        setTimeout(() => {
                            const dropdownMenu = dropdownWrapper.querySelector('.dropdownMenu');
                            if (dropdownMenu) {
                                // Ensure menu and all its children have pointer events
                                dropdownMenu.style.pointerEvents = 'auto';
                                dropdownMenu.style.zIndex = '10001';
                                const allItems = dropdownMenu.querySelectorAll('*');
                                allItems.forEach(item => {
                                    item.style.pointerEvents = 'auto';
                                });
                                
                                // Scroll to current time option
                                const currentTime = getCurrentTime();
                                // Round current time to nearest 5-minute interval
                                const [hours, minutes] = currentTime.split(':').map(Number);
                                const roundedMinutes = Math.floor(minutes / 5) * 5;
                                const roundedTime = `${String(hours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
                                const dayTimeOptions = getTimeOptionsForDate(attendanceData?.[index]?.fullDate);
                                
                                // Find the option that matches current time
                                const currentTimeOption = dayTimeOptions.find(opt => opt.value === roundedTime);
                                if (currentTimeOption) {
                                    // Wait a bit more for dropdown items to render
                                    setTimeout(() => {
                                        // Find all dropdown items (li.dropdownItem)
                                        const dropdownItems = dropdownMenu.querySelectorAll('li.dropdownItem');
                                        
                                        // Find the item that matches the current time label
                                        let targetItem = null;
                                        dropdownItems.forEach(item => {
                                            const itemText = item.textContent?.trim();
                                            // Check if this item matches the current time label
                                            if (itemText === currentTimeOption.label) {
                                                targetItem = item;
                                            }
                                        });
                                        
                                        // Scroll to the current time option instantly (no animation)
                                        if (targetItem) {
                                            // Calculate scroll position to center the item instantly
                                            const itemTop = targetItem.offsetTop;
                                            const itemHeight = targetItem.offsetHeight;
                                            const menuHeight = dropdownMenu.clientHeight;
                                            const scrollPosition = itemTop - (menuHeight / 2) + (itemHeight / 2);
                                            dropdownMenu.scrollTop = Math.max(0, scrollPosition);
                                        } else {
                                            // Fallback: calculate scroll position based on option index
                                            const optionIndex = dayTimeOptions.findIndex(opt => opt.value === roundedTime);
                                            if (optionIndex !== -1) {
                                                // Estimate scroll position (each option is roughly 35-40px, plus search bar ~50px)
                                                const estimatedScroll = (optionIndex + 1) * 38 + 50;
                                                dropdownMenu.scrollTop = Math.max(0, estimatedScroll - dropdownMenu.clientHeight / 2);
                                            }
                                        }
                                    }, 100); // Small delay to ensure dropdown items are rendered
                                }
                                
                                // Disable hover effect on table row when dropdown is open
                                if (tableRow) {
                                    tableRow.style.backgroundColor = 'transparent';
                                    
                                    // Monitor when dropdown closes to re-enable hover
                                    const observer = new MutationObserver((mutations) => {
                                        mutations.forEach((mutation) => {
                                            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                                                const isVisible = dropdownMenu.style.display === 'block' || 
                                                                 getComputedStyle(dropdownMenu).display === 'block';
                                                if (!isVisible && tableRow) {
                                                    tableRow.style.backgroundColor = '';
                                                    observer.disconnect();
                                                }
                                            }
                                        });
                                    });
                                    
                                    observer.observe(dropdownMenu, { 
                                        attributes: true,
                                        attributeFilter: ['style']
                                    });
                                    
                                    // Also listen for clicks outside to clean up
                                    const handleClickOutside = (event) => {
                                        if (!dropdownMenu.contains(event.target) && 
                                            !timeInputGroup.contains(event.target)) {
                                            if (tableRow) {
                                                tableRow.style.backgroundColor = '';
                                            }
                                            document.removeEventListener('mousedown', handleClickOutside);
                                            observer.disconnect();
                                        }
                                    };
                                    
                                    // Use a small delay to avoid immediate trigger
                                    setTimeout(() => {
                                        document.addEventListener('mousedown', handleClickOutside);
                                    }, 100);
                                }
                            }
                        }, 50);
                    }
                }
            }
            
            delete clickTimersRef.current[clickKey];
        }, 300); // Wait 300ms to detect double click
    };

    // Handle double click - focus input for typing
    const handleTimeInputDoubleClick = (e, index, field) => {
        const clickKey = `${index}-${field}`;
        
        // Cancel single click timer
        if (clickTimersRef.current[clickKey]) {
            clearTimeout(clickTimersRef.current[clickKey]);
            delete clickTimersRef.current[clickKey];
        }
        
        // Focus the input so user can type
        const inputElement = e.target;
        inputElement.focus();
        inputElement.select(); // Select all text for easy editing
    };

    // Handle dropdown time selection
    const handleTimeDropdownSelect = (index, field, option) => {
        // Use option.value which preserves the "+1" suffix for next-day times
        // (option.timeValue strips "+1" info so we avoid it here)
        const rawValue = option?.value ?? option?.timeValue ?? option;
        handleTimeChange(index, field, rawValue, true);
    };

    // Get dropdown selected option value, preserving (+1) selection on overnight schedules
    const getDropdownSelectedValue = (dayData, index, field) => {
        const dayKey = `${index}`;
        const edits = editedTimes[dayKey];
        const editExpansionKey = field === 'checkin_daily' ? 'checkin_daily_expansion' : 'checkout_daily_expansion';
        
        let value = '';
        if (edits && edits[field] !== undefined) {
            value = edits[field] || '';
        } else {
            value = dayData[field] || '';
        }
        
        if (!value) return '';
        
        // Convert HH:mm:ss to HH:mm (formatTimeToHHMM handles this)
        const time24 = formatTimeToHHMM(value);
        if (!time24) return '';

        // Use explicitly stored expansion flag (set when user picks from dropdown)
        if (edits && edits[editExpansionKey] !== undefined) {
            return edits[editExpansionKey] === 1 ? `${time24}+1` : time24;
        }

        const scheduleStart = formatTimeToHHMM(dayData.schedule?.checkin);
        const scheduleEnd = formatTimeToHHMM(dayData.schedule?.checkout);

        return getExtendedDropdownSelectedValue({
            value: time24,
            anchorTime: scheduleStart,
            isOutletExtended: true,
            crossesMidnight: doesTimeRangeCrossMidnight(scheduleStart, scheduleEnd),
        });
    };

    const handleDeleteTime = (index, field) => {
        const dayKey = `${index}`;
        const dayData = attendanceData[index];
        
        // If trying to delete check-in and checkout exists, show error
        if (field === 'checkin_daily') {
            const currentCheckout = editedTimes[dayKey]?.checkout_daily !== undefined 
                ? editedTimes[dayKey]?.checkout_daily 
                : dayData.checkout_daily;
            
            if (currentCheckout) {
                toast.warning('Please remove check-out time first before deleting check-in.');
                return;
            }
        }
        
        setEditedTimes(prev => ({
            ...prev,
            [dayKey]: {
                ...prev[dayKey],
                [field]: null
            }
        }));
    };

    const getComparableMinutes = (timeStr, scheduleStart, scheduleEnd, dateInput, expansionOverride = null) => {
        if (!timeStr) return null;

        const normalizedTime = formatTimeToHHMM(timeStr);
        if (!normalizedTime) return null;

        const parts = normalizedTime.split(':');
        if (parts.length < 2) return null;

        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

        let totalMinutes = hours * 60 + minutes;

        // Explicit +1 from dropdown or API — treat as next calendar day
        if (expansionOverride === 1) {
            totalMinutes += 1440;
            return totalMinutes;
        }

        // Explicit same-day (normal) time — do not auto-apply +1
        if (expansionOverride === 0) {
            return totalMinutes;
        }

        // No explicit flag — treat a time before the schedule start as next day when the shift crosses midnight
        const scheduleStartValue = formatTimeToHHMM(scheduleStart);
        const crossesMidnight = doesTimeRangeCrossMidnight(scheduleStartValue, formatTimeToHHMM(scheduleEnd));

        if (crossesMidnight && scheduleStartValue && normalizedTime < scheduleStartValue) {
            totalMinutes += 1440;
        }

        return totalMinutes;
    };

    // Resolve expansion flag for a field.
    // Priority: 1) explicitly stored from dropdown selection, 2) auto-detect, 3) API flag from dayData.
    const resolveExpansionFlag = (dayData, edits, field) => {
        const apiExpansionKey = field === 'checkin_daily' ? 'is_checkin_calendar_expansion' : 'is_checkout_calendar_expansion';
        const editExpansionKey = field === 'checkin_daily' ? 'checkin_daily_expansion' : 'checkout_daily_expansion';

        if (edits?.[field] !== undefined) {
            // Use explicitly stored expansion (set when user picks +1 from dropdown)
            if (edits[editExpansionKey] !== undefined) {
                return edits[editExpansionKey];
            }
            // Fall back to auto-detection from the schedule times
            return getAttendanceExpansionFlag(
                edits[field],
                dayData.schedule?.checkin,
                dayData.schedule?.checkout,
                dayData.fullDate
            );
        }
        return Number(dayData[apiExpansionKey]) || 0;
    };

    // Validate that checkout time is strictly after checkin time (same time is not allowed)
    const validateCheckoutAfterCheckin = (checkinTime, checkoutTime, scheduleStart, scheduleEnd, dateInput, checkinExpansion = null, checkoutExpansion = null) => {
        if (!checkinTime || !checkoutTime) {
            return true; // If either is missing, validation passes (handled separately)
        }

        const checkinMinutes = getComparableMinutes(checkinTime, scheduleStart, scheduleEnd, dateInput, checkinExpansion);
        const checkoutMinutes = getComparableMinutes(checkoutTime, scheduleStart, scheduleEnd, dateInput, checkoutExpansion);
        
        if (checkinMinutes === null || checkoutMinutes === null) {
            return true; // Invalid format, let other validations handle it
        }
        
        return checkoutMinutes > checkinMinutes;
    };

    const getAttendanceExpansionFlag = (timeValue, scheduleStart, scheduleEnd, dateInput) => {
        const normalizedTime = formatTimeToHHMM(timeValue);
        if (!normalizedTime) return 0;

        const normalizedScheduleStart = formatTimeToHHMM(scheduleStart);
        const normalizedScheduleEnd = formatTimeToHHMM(scheduleEnd);
        const selectedValue = getExtendedDropdownSelectedValue({
            value: normalizedTime,
            anchorTime: normalizedScheduleStart,
            isOutletExtended: true,
            crossesMidnight: doesTimeRangeCrossMidnight(normalizedScheduleStart, normalizedScheduleEnd),
        });

        return typeof selectedValue === 'string' && selectedValue.endsWith('+1') ? 1 : 0;
    };

    const getAttendancePayloadExpansionFlags = (dayData, checkinValue, checkoutValue, checkinExpansionOverride = null, checkoutExpansionOverride = null) => {
        const scheduleStart = dayData?.schedule?.checkin;
        const scheduleEnd = dayData?.schedule?.checkout;
        const dateInput = dayData?.fullDate;

        return {
            is_checkin_calendar_expansion: checkinExpansionOverride !== null
                ? checkinExpansionOverride
                : getAttendanceExpansionFlag(checkinValue, scheduleStart, scheduleEnd, dateInput),
            is_checkout_calendar_expansion: checkoutExpansionOverride !== null
                ? checkoutExpansionOverride
                : getAttendanceExpansionFlag(checkoutValue, scheduleStart, scheduleEnd, dateInput),
        };
    };

    const shouldShowFieldExpansionLabel = (dayData, index, field) => {
        const dayKey = `${index}`;
        const editExpansionKey = field === 'checkin_daily' ? 'checkin_daily_expansion' : 'checkout_daily_expansion';
        const hasEditedValue = editedTimes?.[dayKey]?.[field] !== undefined;

        if (hasEditedValue) {
            // Use explicitly stored expansion flag from dropdown selection
            if (editedTimes?.[dayKey]?.[editExpansionKey] !== undefined) {
                return editedTimes[dayKey][editExpansionKey] === 1;
            }
            const selectedValue = getDropdownSelectedValue(dayData, index, field);
            return typeof selectedValue === 'string' && selectedValue.endsWith('+1');
        }

        if (field === 'checkin_daily') {
            return Number(dayData?.is_checkin_calendar_expansion) === 1;
        }

        if (field === 'checkout_daily') {
            return Number(dayData?.is_checkout_calendar_expansion) === 1;
        }

        return false;
    };

    // Optimistically update attendance data in UI immediately
    const updateAttendanceOptimistically = (index, updates) => {
        setAttendanceData(prev => {
            const newData = [...prev];
            if (newData[index]) {
                newData[index] = {
                    ...newData[index],
                    ...updates
                };
            }
            return newData;
        });
    };

    // Check if API response matches optimistic update, only fetch if different
    const verifyAndSyncData = async (index, expectedCheckin, expectedCheckout, apiResponse) => {
        // Check if response contains updated data
        const responseData = apiResponse?.data?.data || apiResponse?.data;
        
        if (responseData) {
            // Get the updated times from API response
            const apiCheckin = responseData.checkin_daily || null;
            const apiCheckout = responseData.checkout_daily || null;
            
            // Format expected values for comparison (remove seconds if present)
            const formatForCompare = (time) => {
                if (!time) return null;
                if (time.includes(':')) {
                    const parts = time.split(':');
                    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
                }
                return time;
            };
            
            const expectedCheckinFormatted = formatForCompare(expectedCheckin);
            const apiCheckinFormatted = formatForCompare(apiCheckin);
            const expectedCheckoutFormatted = formatForCompare(expectedCheckout);
            const apiCheckoutFormatted = formatForCompare(apiCheckout);
            
            // If times don't match, fetch to sync with server
            if (expectedCheckinFormatted !== apiCheckinFormatted || 
                expectedCheckoutFormatted !== apiCheckoutFormatted) {
                await fetchAttendanceData(false);
            }
            // If they match, optimistic update is correct, no need to fetch
        } else {
            // No response data, fetch to get current state
            await fetchAttendanceData(false);
        }
    };

    const handleAction = async (dayData, index) => {
        const dayKey = `${index}`;
        const edits = editedTimes[dayKey] || {};
        
        // Get timing_code_id from either root level or schedule
        const timingCodeId = dayData.timing_code_id || dayData.schedule?.timing_code_id;
        
        // Validate not future date
        if (isFutureDate(dayData.fullDate)) {
            toast.error('Cannot edit future date.');
            return;
        }

        // Check if schedule times are available
        if (!dayData.schedule?.checkin || !dayData.schedule?.checkout) {
            toast.error('Therapist schedule not available for this day.');
            return;
        }
        
        try {
            setActionLoading(true);
            
            const isPast = isPastDate(dayData.fullDate);
            
            // For past dates, always use update API (allow editing both times together)
            if (isPast) {
                // Get final values for checkin and checkout
                const finalCheckin = edits.checkin_daily !== undefined ? edits.checkin_daily : dayData.checkin_daily;
                const finalCheckout = edits.checkout_daily !== undefined ? edits.checkout_daily : dayData.checkout_daily;
                
                // Check if user hasn't edited anything at all
                if (edits.checkin_daily === undefined && edits.checkout_daily === undefined) {
                    toast.warning('Please edit a time before updating.');
                    setActionLoading(false);
                    return;
                }

                // Validate that checkout time is not before checkin time (only if both are present)
                if (finalCheckin && finalCheckout && !validateCheckoutAfterCheckin(
                    finalCheckin,
                    finalCheckout,
                    dayData.schedule?.checkin,
                    dayData.schedule?.checkout,
                    dayData.fullDate,
                    resolveExpansionFlag(dayData, edits, 'checkin_daily'),
                    resolveExpansionFlag(dayData, edits, 'checkout_daily')
                )) {
                    toast.error('Check-out must be later than check-in');
                    setActionLoading(false);
                    return;
                }

                // Optimistically update UI immediately
                updateAttendanceOptimistically(index, {
                    checkin_daily: finalCheckin || null,
                    checkout_daily: finalCheckout || null
                });

                // Use the update API for past dates - allow null values
                const updateData = {
                    user: therapistId,
                    event_date: dayData.formattedDate,
                    timing_code_id: timingCodeId,
                    checkin: finalCheckin ? formatTimeToHHMM(finalCheckin) : null,
                    checkout: finalCheckout ? formatTimeToHHMM(finalCheckout) : null,
                    ...getAttendancePayloadExpansionFlags(dayData, finalCheckin, finalCheckout,
                        resolveExpansionFlag(dayData, edits, 'checkin_daily'),
                        resolveExpansionFlag(dayData, edits, 'checkout_daily')
                    )
                };

                const response = await updateCheckinCheckout(updateData);
                
                const isSuccess = response?.success || response?.data?.success;
                
                if (isSuccess) {
                    setEditedTimes(prev => {
                        const newEdits = { ...prev };
                        delete newEdits[dayKey];
                        return newEdits;
                    });
                    
                    toast.success('Successfully updated attendance!');
                    
                    // Verify if API response matches optimistic update, only fetch if different
                    await verifyAndSyncData(index, finalCheckin, finalCheckout, response);
                    
                    // Trigger refresh of therapist list and header
                    if (onAttendanceUpdate) {
                        onAttendanceUpdate();
                    }
                } else {
                    // Revert optimistic update on error
                    await fetchAttendanceData(false);
                    const errorMessage = response?.message || response?.data?.message || 'Failed to update attendance';
                    toast.error(errorMessage);
                }
                
                setActionLoading(false);
                return;
            }
            
            // For today's date, use sequential check-in/checkout flow
            let dataToSend = {
                user: therapistId,
                event_date: dayData.formattedDate,
                timing_code_id: timingCodeId
            };

            let actionType = '';

            // Determine action based on current state for today
            if (!dayData.checkin_daily) {
                // No check-in exists - if no time selected, use current time and call API directly
                let checkinTime = edits.checkin_daily;
                if (!checkinTime) {
                    // Use current time and proceed with API call
                    const currentTime = getCurrentTime();
                    checkinTime = currentTime;
                }
                
                // Format check-in time (ensure it has seconds format if needed)
                const formattedCheckinTime = checkinTime.includes(':') && checkinTime.split(':').length === 2 
                    ? `${checkinTime}:00` 
                    : checkinTime;
                
                // Optimistically update UI immediately
                updateAttendanceOptimistically(index, {
                    checkin_daily: formattedCheckinTime,
                    checkout_daily: null
                });
                
                // Proceed with API call (either with selected time or current time)
                dataToSend.type = 'checkin';
                // Only send timing when user picked a time from the dropdown
                if (edits.checkin_daily) {
                    dataToSend.timing = formatTimeToHHMM(checkinTime);
                }
                Object.assign(
                    dataToSend,
                    getAttendancePayloadExpansionFlags(dayData, checkinTime, null,
                        edits.checkin_daily !== undefined ? resolveExpansionFlag(dayData, edits, 'checkin_daily') : 0,
                        null
                    )
                );
                actionType = 'checked in';
            } else if (dayData.checkin_daily && !dayData.checkout_daily) {
                // Check-in exists, no check-out
                // If check-in is being edited, use update API
                if (edits.checkin_daily !== undefined) {
                    const finalCheckin = edits.checkin_daily;
                    
                    // Format check-in time (ensure it has seconds format if needed)
                    const formattedCheckinTime = finalCheckin && finalCheckin.includes(':') && finalCheckin.split(':').length === 2 
                        ? `${finalCheckin}:00` 
                        : finalCheckin;
                    
                    // Optimistically update UI immediately
                    updateAttendanceOptimistically(index, {
                        checkin_daily: formattedCheckinTime,
                        checkout_daily: null
                    });
                    
                    // Use update API to update check-in time (check-out can be null)
                    const updateData = {
                        user: therapistId,
                        event_date: dayData.formattedDate,
                        timing_code_id: timingCodeId,
                        checkin: finalCheckin ? formatTimeToHHMM(finalCheckin) : null,
                        checkout: null, // No check-out exists
                        ...getAttendancePayloadExpansionFlags(dayData, finalCheckin, null,
                            resolveExpansionFlag(dayData, edits, 'checkin_daily'),
                            null
                        )
                    };

                    const response = await updateCheckinCheckout(updateData);
                    
                    const isSuccess = response?.success || response?.data?.success;
                    
                    if (isSuccess) {
                        setEditedTimes(prev => {
                            const newEdits = { ...prev };
                            delete newEdits[dayKey];
                            return newEdits;
                        });
                        
                        toast.success('Successfully updated check-in time!');
                        
                        // Verify if API response matches optimistic update, only fetch if different
                        await verifyAndSyncData(index, finalCheckin, null, response);
                        
                        if (onAttendanceUpdate) {
                            onAttendanceUpdate();
                        }
                    } else {
                        // Revert optimistic update on error
                        await fetchAttendanceData(false);
                        const errorMessage = response?.message || response?.data?.message || 'Failed to update check-in time';
                        toast.error(errorMessage);
                    }
                    
                    setActionLoading(false);
                    return;
                }
                
                // Check-out is being added (not check-in edited) - use checkout API
                let checkoutTime = edits.checkout_daily;
                if (!checkoutTime) {
                    // Use current time and proceed with API call
                    const currentTime = getCurrentTime();
                    checkoutTime = currentTime;
                }
                
                // Validate that checkout time is not before checkin time
                if (!validateCheckoutAfterCheckin(
                    dayData.checkin_daily,
                    checkoutTime,
                    dayData.schedule?.checkin,
                    dayData.schedule?.checkout,
                    dayData.fullDate,
                    Number(dayData.is_checkin_calendar_expansion) || 0,
                    resolveExpansionFlag(dayData, edits, 'checkout_daily')
                )) {
                    toast.error('Check-out must be later than check-in');
                    setActionLoading(false);
                    return;
                }
                
                // Format check-out time (ensure it has seconds format if needed)
                const formattedCheckoutTime = checkoutTime.includes(':') && checkoutTime.split(':').length === 2 
                    ? `${checkoutTime}:00` 
                    : checkoutTime;
                
                // Optimistically update UI immediately
                updateAttendanceOptimistically(index, {
                    checkin_daily: dayData.checkin_daily, // Keep existing check-in
                    checkout_daily: formattedCheckoutTime
                });
                
                // Proceed with API call (either with selected time or current time)
                dataToSend.type = 'checkout';
                // Only send timing when user picked a time from the dropdown
                if (edits.checkout_daily) {
                    dataToSend.timing = formatTimeToHHMM(checkoutTime);
                }
                Object.assign(
                    dataToSend,
                    getAttendancePayloadExpansionFlags(dayData, dayData.checkin_daily, checkoutTime,
                        Number(dayData.is_checkin_calendar_expansion) || 0,
                        resolveExpansionFlag(dayData, edits, 'checkout_daily')
                    )
                );
                actionType = 'checked out';
            } else if (dayData.checkin_daily && dayData.checkout_daily) {
                // Both exist for today - use update API
                const finalCheckin = edits.checkin_daily !== undefined ? edits.checkin_daily : dayData.checkin_daily;
                const finalCheckout = edits.checkout_daily !== undefined ? edits.checkout_daily : dayData.checkout_daily;
                
                // Check if user hasn't edited anything at all
                if (edits.checkin_daily === undefined && edits.checkout_daily === undefined) {
                    toast.warning('Please edit a time before updating.');
                    setActionLoading(false);
                    return;
                }

                // Validate that checkout time is not before checkin time (only if both are present)
                if (finalCheckin && finalCheckout && !validateCheckoutAfterCheckin(
                    finalCheckin,
                    finalCheckout,
                    dayData.schedule?.checkin,
                    dayData.schedule?.checkout,
                    dayData.fullDate,
                    resolveExpansionFlag(dayData, edits, 'checkin_daily'),
                    resolveExpansionFlag(dayData, edits, 'checkout_daily')
                )) {
                    toast.error('Check-out must be later than check-in');
                    setActionLoading(false);
                    return;
                }

                // Format times (ensure they have seconds format if needed, only if not null)
                const formattedCheckin = finalCheckin && finalCheckin.includes(':') && finalCheckin.split(':').length === 2 
                    ? `${finalCheckin}:00` 
                    : finalCheckin || null;
                const formattedCheckout = finalCheckout && finalCheckout.includes(':') && finalCheckout.split(':').length === 2 
                    ? `${finalCheckout}:00` 
                    : finalCheckout || null;

                // Optimistically update UI immediately
                updateAttendanceOptimistically(index, {
                    checkin_daily: formattedCheckin,
                    checkout_daily: formattedCheckout
                });

                // Use the update API
                const updateData = {
                    user: therapistId,
                    event_date: dayData.formattedDate,
                    timing_code_id: timingCodeId,
                    checkin: finalCheckin ? formatTimeToHHMM(finalCheckin) : null,
                    checkout: finalCheckout ? formatTimeToHHMM(finalCheckout) : null,
                    ...getAttendancePayloadExpansionFlags(dayData, finalCheckin, finalCheckout,
                        resolveExpansionFlag(dayData, edits, 'checkin_daily'),
                        resolveExpansionFlag(dayData, edits, 'checkout_daily')
                    )
                };

                const response = await updateCheckinCheckout(updateData);
                
                const isSuccess = response?.success || response?.data?.success;
                
                if (isSuccess) {
                    setEditedTimes(prev => {
                        const newEdits = { ...prev };
                        delete newEdits[dayKey];
                        return newEdits;
                    });
                    
                    toast.success('Successfully updated attendance!');
                    
                    // Verify if API response matches optimistic update, only fetch if different
                    await verifyAndSyncData(index, finalCheckin, finalCheckout, response);
                    
                    // Trigger refresh of therapist list and header
                    if (onAttendanceUpdate) {
                        onAttendanceUpdate();
                    }
                } else {
                    // Revert optimistic update on error
                    await fetchAttendanceData(false);
                    const errorMessage = response?.message || response?.data?.message || 'Failed to update attendance';
                    toast.error(errorMessage);
                }
                
                setActionLoading(false);
                return;
            }

            // Use saveEmployeeAttendance for sequential check-in/checkout on today
            const response = await saveEmployeeAttendance(dataToSend);
            
            // Check both possible response formats
            const isSuccess = response?.success || response?.data?.success;
            
            if (isSuccess) {
                // Clear edits for this day
                setEditedTimes(prev => {
                    const newEdits = { ...prev };
                    delete newEdits[dayKey];
                    return newEdits;
                });
                
                // Show success message
                toast.success(`Successfully ${actionType}!`);
                
                // Determine expected values based on action type
                let expectedCheckin = dayData.checkin_daily;
                let expectedCheckout = dayData.checkout_daily;
                
                if (actionType === 'checked in') {
                    const checkinTime = edits.checkin_daily || getCurrentTime();
                    expectedCheckin = checkinTime.includes(':') && checkinTime.split(':').length === 2 
                        ? `${checkinTime}:00` 
                        : checkinTime;
                    expectedCheckout = null;
                } else if (actionType === 'checked out') {
                    const checkoutTime = edits.checkout_daily || getCurrentTime();
                    expectedCheckin = dayData.checkin_daily;
                    expectedCheckout = checkoutTime.includes(':') && checkoutTime.split(':').length === 2 
                        ? `${checkoutTime}:00` 
                        : checkoutTime;
                }
                
                // Verify if API response matches optimistic update, only fetch if different
                await verifyAndSyncData(index, expectedCheckin, expectedCheckout, response);
                
                // Trigger refresh of therapist list and header
                if (onAttendanceUpdate) {
                    onAttendanceUpdate();
                }
            } else {
                // Revert optimistic update on error
                await fetchAttendanceData(false);
                const errorMessage = response?.message || response?.data?.message || 'Failed to save attendance';
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('Error saving attendance:', error);
            // Revert optimistic update on error
            await fetchAttendanceData(false);
            toast.error('An error occurred while saving attendance');
        } finally {
            setActionLoading(false);
        }
    };

    const getDisplayValue = (dayData, index, field) => {
        const dayKey = `${index}`;
        const edits = editedTimes[dayKey];
        
        let value = '';
        if (edits && edits[field] !== undefined) {
            value = edits[field] || '';
        } else {
            value = dayData[field] || '';
        }
        
        // If no value, return empty
        if (!value) return '';
        
        // If value contains :00:00 (formatted API value), convert to HH:mm for display
        if (value.includes(':') && value.split(':').length === 3) {
            const parts = value.split(':');
            return `${parts[0]}:${parts[1]}`;
        }
        
        // Otherwise return as-is (user is typing)
        return value;
    };

    const formatScheduleTime = (checkin, checkout, isExpanded = false) => {
        if (!checkin || !checkout) return '-';
        
        // Remove seconds if present
        const formatTime = (time) => {
            if (!time) return '';
            const parts = time.split(':');
            return `${parts[0]}:${parts[1]}`;
        };
        
        return `${formatTime(checkin)} - ${formatTime(checkout)}${isExpanded ? ' +1' : ''}`;
    };

    const getTimingCodeById = (timingCodeId) => {
        if (!timingCodeId) return 'Custom schedule';
        return timingCodes[timingCodeId] || 'Custom schedule';
    };

    if (loading && attendanceData.length === 0) {
        return (
            <div className={styles.loaderContainer}>
                <Loader />
            </div>
        );
    }

    return (
        <div className={styles.attendanceContainer}>
            {loading && attendanceData.length > 0 && (
                <div className={styles.loaderOverlay}>
                    <Loader />
                </div>
            )}
            <div className={styles.tableWrapper}>
                <table className={styles.attendanceTable}>
                    <thead>
                        <tr>
                            <th>Day</th>
                            <th>Schedule</th>
                            <th>Check In</th>
                            <th>Check Out</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {attendanceData.map((dayData, index) => {
                            const canEdit = isTodayOrPast(dayData.fullDate);
                            const hasSchedule = dayData.schedule !== null;
                            const hasScheduleTimes = hasSchedule && dayData.schedule.checkin && dayData.schedule.checkout;
                            const isFuture = isFutureDate(dayData.fullDate);
                            
                            // Determine tooltip message
                            const isLeave = dayData.leave === 1;
                            let tooltipMessage = '';
                            if (isFuture) {
                                tooltipMessage = 'Cannot edit future date';
                            } else if (isLeave) {
                                tooltipMessage = 'Therapist is off';
                            } else if (!hasScheduleTimes) {
                                tooltipMessage = 'Please add therapist schedule.';
                            }
                            
                            return (
                                <tr key={index} title={isFuture ? 'Cannot edit future date' : undefined}>
                                    <td>
                                        <div className={styles.dayCell}>
                                            <span className={styles.dayName}>{dayData.dayName}</span>
                                            <span className={styles.date}>{dayData.dateStr} {dayData.monthStr}</span>
                                        </div>
                                    </td>
                                    <td>
                                        {dayData.leave === 1 ? (
                                            <div className={styles.scheduleCell}>
                                                <span className={styles.scheduleCode}>OFF</span>
                                                <span className={styles.scheduleTime}>
                                                    {dayData.status && dayData.remark 
                                                        ? `${dayData.status} - ${dayData.remark}`
                                                        : dayData.status 
                                                        ? dayData.status
                                                        : dayData.remark 
                                                        ? dayData.remark
                                                        : ''
                                                    }
                                                </span>
                                            </div>
                                        ) : hasScheduleTimes ? (
                                            <div className={styles.scheduleCell}>
                                                <span className={styles.scheduleCode}>{getTimingCodeById(dayData.schedule.timing_code_id)}</span>
                                                <span className={styles.scheduleTime}>
                                                    {formatScheduleTime(
                                                        dayData.schedule.checkin,
                                                        dayData.schedule.checkout,
                                                        Number(dayData.is_schedule_calendar_expansion) === 1
                                                    )}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className={styles.noSchedule} title={tooltipMessage || undefined}>-</span>
                                        )}
                                    </td>
                                    <td>
                                        {(() => {
                                            const isPast = isPastDate(dayData.fullDate);
                                            
                                            // For past dates: Always show input if schedule exists and leave !== 1
                                            if (isPast && hasScheduleTimes && dayData.leave !== 1) {
                                                const currentValue = getDisplayValue(dayData, index, 'checkin_daily');
                                                return (
                                                    <div className={styles.timeInputGroup}>
                                                        <div className={styles.timeInputWrapper}>
                                                            <input
                                                                type="time"
                                                                className={styles.timeInput}
                                                                value={currentValue}
                                                                onChange={(e) => handleTimeChange(index, 'checkin_daily', e.target.value)}
                                                                onClick={(e) => handleTimeInputClick(e, index, 'checkin_daily', currentValue)}
                                                                onDoubleClick={(e) => handleTimeInputDoubleClick(e, index, 'checkin_daily')}
                                                                step="60"
                                                            />
                                                            {currentValue && (
                                                                <button
                                                                    className={styles.deleteBtn}
                                                                    onClick={() => handleDeleteTime(index, 'checkin_daily')}
                                                                    title="Delete check-in time"
                                                                >
                                                                    <img src={deleteIcon} alt="Delete" />
                                                                </button>
                                                            )}
                                                            {shouldShowFieldExpansionLabel(dayData, index, 'checkin_daily') && (
                                                                <span className={styles.timeExpansionSuffix}>+1</span>
                                                            )}
                                                        </div>
                                                        <div className={styles.timeDropdownWrapper}>
                                                            <DropdownNoLabel
                                                                options={getTimeOptionsForDate(dayData.fullDate)}
                                                                selectedLabel=""
                                                                selectedValue={getDropdownSelectedValue(dayData, index, 'checkin_daily')}
                                                                onSelect={(option) => handleTimeDropdownSelect(index, 'checkin_daily', option)}
                                                                dropdownClass={`${styles.timeDropdown} ${styles.hiddenDropdownToggle}`}
                                                                placeholder=""
                                                                placeholderClass={styles.placeholder}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            
                                            // For today: Show input if schedule exists and can edit
                                            // This allows editing check-in even when check-out is missing
                                            if (hasScheduleTimes && canEdit && !isFuture && dayData.leave !== 1) {
                                                const currentValue = getDisplayValue(dayData, index, 'checkin_daily');
                                                return (
                                                    <div className={styles.timeInputGroup}>
                                                        <div className={styles.timeInputWrapper}>
                                                            <input
                                                                type="time"
                                                                className={styles.timeInput}
                                                                value={currentValue}
                                                                onChange={(e) => handleTimeChange(index, 'checkin_daily', e.target.value)}
                                                                onClick={(e) => handleTimeInputClick(e, index, 'checkin_daily', currentValue)}
                                                                onDoubleClick={(e) => handleTimeInputDoubleClick(e, index, 'checkin_daily')}
                                                                step="60"
                                                            />
                                                            {currentValue && (
                                                                <button
                                                                    className={styles.deleteBtn}
                                                                    onClick={() => handleDeleteTime(index, 'checkin_daily')}
                                                                    title="Delete check-in time"
                                                                >
                                                                    <img src={deleteIcon} alt="Delete" />
                                                                </button>
                                                            )}
                                                            {shouldShowFieldExpansionLabel(dayData, index, 'checkin_daily') && (
                                                                <span className={styles.timeExpansionSuffix}>+1</span>
                                                            )}
                                                        </div>
                                                        <div className={styles.timeDropdownWrapper}>
                                                            <DropdownNoLabel
                                                                options={getTimeOptionsForDate(dayData.fullDate)}
                                                                selectedLabel=""
                                                                selectedValue={getDropdownSelectedValue(dayData, index, 'checkin_daily')}
                                                                onSelect={(option) => handleTimeDropdownSelect(index, 'checkin_daily', option)}
                                                                dropdownClass={`${styles.timeDropdown} ${styles.hiddenDropdownToggle}`}
                                                                placeholder=""
                                                                placeholderClass={styles.placeholder}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            
                                            // Otherwise show dash
                                            return <span className={styles.noSchedule} title={tooltipMessage || undefined}>-</span>;
                                        })()}
                                    </td>
                                    <td>
                                        {(() => {
                                            const isPast = isPastDate(dayData.fullDate);
                                            
                                            // For past dates: Always show input if schedule exists and leave !== 1
                                            if (isPast && hasScheduleTimes && dayData.leave !== 1) {
                                                const currentValue = getDisplayValue(dayData, index, 'checkout_daily');
                                                return (
                                                    <div className={styles.timeInputGroup}>
                                                        <div className={styles.timeInputWrapper}>
                                                            <input
                                                                type="time"
                                                                className={styles.timeInput}
                                                                value={currentValue}
                                                                onChange={(e) => handleTimeChange(index, 'checkout_daily', e.target.value)}
                                                                onClick={(e) => handleTimeInputClick(e, index, 'checkout_daily', currentValue)}
                                                                onDoubleClick={(e) => handleTimeInputDoubleClick(e, index, 'checkout_daily')}
                                                                step="60"
                                                            />
                                                            {currentValue && (
                                                                <button
                                                                    className={styles.deleteBtn}
                                                                    onClick={() => handleDeleteTime(index, 'checkout_daily')}
                                                                    title="Delete check-out time"
                                                                >
                                                                    <img src={deleteIcon} alt="Delete" />
                                                                </button>
                                                            )}
                                                            {shouldShowFieldExpansionLabel(dayData, index, 'checkout_daily') && (
                                                                <span className={styles.timeExpansionSuffix}>+1</span>
                                                            )}
                                                        </div>
                                                        <div className={styles.timeDropdownWrapper}>
                                                            <DropdownNoLabel
                                                                options={getTimeOptionsForDate(dayData.fullDate)}
                                                                selectedLabel=""
                                                                selectedValue={getDropdownSelectedValue(dayData, index, 'checkout_daily')}
                                                                onSelect={(option) => handleTimeDropdownSelect(index, 'checkout_daily', option)}
                                                                dropdownClass={`${styles.timeDropdown} ${styles.hiddenDropdownToggle}`}
                                                                placeholder=""
                                                                placeholderClass={styles.placeholder}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            
                                            // For today: Show input only if checked-in (with or without checkout)
                                            if (hasScheduleTimes && canEdit && !isFuture && dayData.leave !== 1 && (dayData.checkin_daily && !dayData.checkout_daily || (dayData.checkin_daily && dayData.checkout_daily))) {
                                                const currentValue = getDisplayValue(dayData, index, 'checkout_daily');
                                                return (
                                                    <div className={styles.timeInputGroup}>
                                                        <div className={styles.timeInputWrapper}>
                                                            <input
                                                                type="time"
                                                                className={styles.timeInput}
                                                                value={currentValue}
                                                                onChange={(e) => handleTimeChange(index, 'checkout_daily', e.target.value)}
                                                                onClick={(e) => handleTimeInputClick(e, index, 'checkout_daily', currentValue)}
                                                                onDoubleClick={(e) => handleTimeInputDoubleClick(e, index, 'checkout_daily')}
                                                                step="60"
                                                            />
                                                            {currentValue && (
                                                                <button
                                                                    className={styles.deleteBtn}
                                                                    onClick={() => handleDeleteTime(index, 'checkout_daily')}
                                                                    title="Delete check-out time"
                                                                >
                                                                    <img src={deleteIcon} alt="Delete" />
                                                                </button>
                                                            )}
                                                            {shouldShowFieldExpansionLabel(dayData, index, 'checkout_daily') && (
                                                                <span className={styles.timeExpansionSuffix}>+1</span>
                                                            )}
                                                        </div>
                                                        <div className={styles.timeDropdownWrapper}>
                                                            <DropdownNoLabel
                                                                options={getTimeOptionsForDate(dayData.fullDate)}
                                                                selectedLabel=""
                                                                selectedValue={getDropdownSelectedValue(dayData, index, 'checkout_daily')}
                                                                onSelect={(option) => handleTimeDropdownSelect(index, 'checkout_daily', option)}
                                                                dropdownClass={`${styles.timeDropdown} ${styles.hiddenDropdownToggle}`}
                                                                placeholder=""
                                                                placeholderClass={styles.placeholder}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            
                                            // Otherwise show dash
                                            return <span className={styles.noSchedule} title={tooltipMessage || undefined}>-</span>;
                                        })()}
                                    </td>
                                    <td>
                                        {(() => {
                                            // Disable button if loading, future date, no schedule, or on leave
                                            const isLeave = dayData.leave === 1;
                                            const isDisabled = actionLoading || isFuture || isLeave || !hasScheduleTimes;
                                            
                                            // Determine tooltip for the button/wrapper
                                            let buttonTooltip = '';
                                            if (isFuture) {
                                                buttonTooltip = 'Cannot edit future date';
                                            } else if (isLeave) {
                                                buttonTooltip = 'Therapist is off';
                                            } else if (!hasScheduleTimes) {
                                                buttonTooltip = 'Please add therapist schedule.';
                                            }

                                            return (
                                                <div 
                                                    title={buttonTooltip}
                                                    onClick={() => {
                                                        if (buttonTooltip && isDisabled) {
                                                            if (!toast.isActive('disabled-reason')) {
                                                                toast.info(buttonTooltip, { toastId: 'disabled-reason' });
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <PrimaryButton
                                                        text={getButtonText(dayData, index)}
                                                        onClick={() => handleAction(dayData, index)}
                                                        disabled={isDisabled}
                                                        className={styles.actionButton}
                                                    />
                                                </div>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Attendance;